import { useState, useContext, useMemo, useCallback, useEffect } from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';

import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  Flex,
  useColorModeValue,
  Grid,
  Stack,
  Box,
  HStack,
  Heading,
  Link,
  Divider,
  Icon,
  Spinner,
  Button,
} from '@chakra-ui/react';

import {
  PhoneIcon,
  EmailIcon,
  InfoIcon,
  CalendarIcon,
  StarIcon,
  CheckIcon,
  CloseIcon,
  DownloadIcon,
  AttachmentIcon,
} from '@chakra-ui/icons';

import LeadStatusChange from 'views/leads/components/LeadStatusChange';
import LeadArchive from 'views/leads/components/LeadArchive';
import LeadAddNote from 'views/leads/components/LeadAddNote';
import LeadFileUpload from 'views/leads/components/LeadFileUpload';
import LeadLog from 'views/lead_overview/components/LeadLog';
import LeadHot from 'views/leads/components/LeadHot';
import UnarchiveLead from 'views/leads/components/UnarchiveLead';
import LeadAssignUser from 'views/leads/components/LeadAssignUser';

import LeadSubmissions from 'views/lead_overview/components/LeadSubmissions/LeadSubmissions';
import { LeadDashReportModal } from 'views/lead_overview/components';

import { apiLeadOverview } from 'api';
import { useViewAs } from 'hooks/useViewAs';

import getDateTimeString from 'helpers/getDateTimeString';
import getDateAge from 'helpers/getDateAge';

import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';
import { PROVINCES } from 'variables/options';

import {
  LEAD_STATUS_ARCHIVED,
  LEAD_STATUS_APPLICATION_IN_PROGRESS,
  LEAD_STATUS_WON,
  LEAD_STATUS_LOST,
} from 'views/leads/constants';

import type {
  APILeadOverviewData,
  APILeadUploadData,
  APIStatusChangeData,
  APILeadUpdateData,
} from 'types/api.types';

import { AuthContext } from 'contexts/AuthContext';

const LeadOverviewView = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const viewAs = useViewAs();

  const searchParams = new URLSearchParams(location.search);
  const shouldOpenSubmission = searchParams.get('openSubmission') === 'true';

  // DASH Report Modal state
  const [isDashModalOpen, setIsDashModalOpen] = useState(false);

  const { error, data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['lead-overview', id, viewAs.filterParams],
    queryFn: () =>
      apiLeadOverview(parseInt(id), viewAs.viewAsEmail, viewAs.viewAsBranchId),
  }) as {
    data: APILeadOverviewData;
    isLoading: boolean;
    error: any;
    refetch: any;
    isRefetching: boolean;
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const noteBgColor = useColorModeValue('gray.50', 'gray.700');

  const onNoteUpdate = useCallback(
    (newData: APILeadUpdateData) => {
      // Update only the note field in the cache
      queryClient.setQueryData(
        ['lead-overview', id, viewAs.filterParams],
        (oldData: APILeadOverviewData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            notes: newData.notes,
          };
        },
      );

      // Invalidate lead-logs for this lead
      queryClient.invalidateQueries({
        queryKey: ['lead-logs', id],
      });
    },
    [id, queryClient, viewAs.filterParams],
  );

  const canEditData = useMemo(() => {
    if (!data) return false;
    if (data.status === LEAD_STATUS_ARCHIVED) return false;
    if ([ROLE_ADMIN, ROLE_MANAGER].includes(user.role)) return true;
    if (!data.user) return false;
    return data.user.id === user.id;
  }, [data, user]);

  const canAssignUser = useMemo(() => {
    if (!data) return false;
    if (data.status === LEAD_STATUS_ARCHIVED) return false;
    return !data.user;
  }, [data]);

  const onFileUploadSuccess = useCallback(
    (newFile: APILeadUploadData) => {
      // Update the React Query cache directly
      queryClient.setQueryData(
        ['lead-overview', id, viewAs.filterParams],
        (oldData: APILeadOverviewData) => {
          if (!oldData) return oldData;

          // Create a new files array that includes the new file
          const updatedFiles = [...(oldData.files || []), newFile];

          // Return a new data object with the updated files
          return {
            ...oldData,
            files: updatedFiles,
          };
        },
      );

      // Invalidate lead-logs for this lead
      queryClient.invalidateQueries({
        queryKey: ['lead-logs', id],
      });
    },
    [id, queryClient, viewAs.filterParams],
  );

  const onStatusChange = useCallback(
    (newStatus: APIStatusChangeData) => {
      queryClient.setQueryData(
        ['lead-overview', id, viewAs.filterParams],
        (oldData: APILeadOverviewData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            status: newStatus.status,
            status_display: newStatus.status_display,
          };
        },
      );

      // Invalidate lead-logs for this lead
      queryClient.invalidateQueries({
        queryKey: ['lead-logs', id],
      });
    },
    [id, queryClient, viewAs.filterParams],
  );

  const onLeadHotChange = useCallback(() => {
    // Invalidate lead-logs for this lead
    queryClient.invalidateQueries({
      queryKey: ['lead-logs', id],
    });
  }, [id, queryClient]);

  // DASH Report Modal handlers
  const handleOpenDashModal = () => {
    setIsDashModalOpen(true);
  };

  const handleCloseDashModal = () => {
    setIsDashModalOpen(false);
  };

  const handleDashReportSuccess = () => {
    // Refresh lead data to show new DASH report
    refetch();
    // Also refresh lead logs to show the new DASH report generation log
    queryClient.invalidateQueries({ queryKey: ['lead-logs', id] });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  // Helper function to identify DASH report files
  const isDashReportFile = (fileName: string) => {
    return fileName.toLowerCase().includes('dash report');
  };

  if (isLoading || !data) {
    return <Card textAlign="center">Loading lead details...</Card>;
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  const formatCurrency = (value: string) => {
    if (!value) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Flex direction="column" gap={6}>
      <Card p={5}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align="center"
        >
          <Box>
            <Heading size="lg" color={textColor}>
              <Text display="inline-flex" mr={2}>
                {data.first_name} {data.last_name}
              </Text>
              {canEditData && (
                <LeadHot lead={data} onSuccess={onLeadHotChange} />
              )}
            </Heading>
            <Flex align="center" mt={1} flexWrap="wrap">
              <Text color={labelColor} mr={3}>
                Created {getDateTimeString(new Date(data.created_at))}
              </Text>

              {data.tags && data.tags.length > 0 && (
                <HStack spacing={1} overflow="hidden">
                  {data.tags.map((tag) => (
                    <Box
                      key={tag.id}
                      bg="blue.100"
                      color="blue.800"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {tag.name}
                    </Box>
                  ))}
                </HStack>
              )}
            </Flex>
          </Box>
          <Flex mt={{ base: 4, md: 0 }} gap={4} align="center">
            {isRefetching && (
              <Flex
                fontSize="xs"
                color={labelColor}
                alignItems="center"
                gap={3}
                mr={3}
              >
                <Spinner /> Updating...
              </Flex>
            )}

            {canAssignUser && (
              <Flex gap={4}>
                <LeadAssignUser
                  lead={data}
                  onSuccess={refetch}
                  size={canEditData ? 'md' : 'sm'}
                />
              </Flex>
            )}

            {canEditData ? (
              <>
                <Box>
                  <Button
                    variant="brand"
                    onClick={() => navigate(`/admin/leads/edit/${data.id}`)}
                    fontSize="sm"
                  >
                    Edit Lead
                  </Button>
                </Box>
                <LeadStatusChange lead={data} onSuccess={onStatusChange} />
                <LeadArchive
                  lead={data}
                  onSuccess={onStatusChange}
                  iconSize="md"
                />
              </>
            ) : (
              <Flex align={'center'} gap={2}>
                <Icon as={CalendarIcon} color="teal.500" />
                <Text
                  fontWeight="medium"
                  color={textColor}
                  display="inline-flex"
                >
                  Status:
                </Text>
                <Text fontSize="sm" display="inline-flex">
                  {data.status_display}
                </Text>
              </Flex>
            )}

            {data.status === LEAD_STATUS_ARCHIVED &&
              [ROLE_ADMIN, ROLE_MANAGER].includes(user.role) && (
                <UnarchiveLead lead={data} onSuccess={onStatusChange} />
              )}
          </Flex>
        </Flex>
      </Card>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
        <Card p={5} h="100%">
          <Heading size="md" mb={4} color={textColor}>
            Contact Information
          </Heading>
          <Divider mb={4} borderColor={borderColor} />

          <Grid templateColumns="1fr 1fr" gap={6}>
            <Stack spacing={4}>
              <Box>
                <HStack mb={1}>
                  <EmailIcon color="blue.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Email
                  </Text>
                  <Flex align="center">
                    <Box
                      as="span"
                      mr={2}
                      color={data.is_casl_compliant ? 'green.500' : 'red.500'}
                    >
                      {data.is_casl_compliant ? (
                        <Icon as={CheckIcon} boxSize={4} />
                      ) : (
                        <Icon as={CloseIcon} boxSize={3} />
                      )}
                    </Box>
                    <Text fontSize="sm">
                      (CASL {data.is_casl_compliant ? 'Opted in' : 'Opted out'})
                    </Text>
                  </Flex>
                </HStack>
                <Text fontSize="sm">{data.email || 'Not provided'}</Text>
              </Box>

              <Box>
                <HStack mb={1}>
                  <PhoneIcon color="green.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Phone
                  </Text>
                </HStack>
                <Text fontSize="sm">{data.phone_number || 'Not provided'}</Text>
              </Box>
            </Stack>

            <Stack spacing={4}>
              <Box>
                <HStack mb={1}>
                  <InfoIcon color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Postal Code
                  </Text>
                </HStack>
                <Text fontSize="sm">{data.postal_code || 'Not provided'}</Text>
              </Box>

              <Box>
                <HStack mb={1}>
                  <InfoIcon color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Province
                  </Text>
                </HStack>
                <Text fontSize="sm">
                  {PROVINCES.find(
                    (province) => province.value === data.province_display,
                  )?.label ||
                    data.province_display ||
                    'Not provided'}
                </Text>
              </Box>
            </Stack>
          </Grid>
        </Card>

        <Card p={5} h="100%">
          <Heading size="md" mb={4} color={textColor}>
            Lead Information
          </Heading>
          <Divider mb={4} borderColor={borderColor} />

          <Grid templateColumns="1fr 1fr 1fr" gap={6}>
            <Stack spacing={4}>
              <Box>
                <HStack mb={1}>
                  <Icon as={StarIcon} color="orange.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Premium
                  </Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold">
                  {formatCurrency(data.value)}
                </Text>
              </Box>

              <Box>
                <HStack mb={1}>
                  <Icon as={InfoIcon} color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Source
                  </Text>
                </HStack>
                <Text fontSize="sm">{data.source?.name || 'None'}</Text>
              </Box>

              {data.handler_name && (
                <Box>
                  <HStack mb={1}>
                    <Icon as={InfoIcon} color="teal.500" />
                    <Text fontWeight="medium" color={textColor}>
                      Processed By
                    </Text>
                  </HStack>
                  <Text fontSize="sm">{data.handler_name}</Text>
                </Box>
              )}
            </Stack>

            <Stack spacing={4}>
              <Box>
                <HStack mb={1}>
                  <Icon as={InfoIcon} color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Workflow
                  </Text>
                </HStack>
                <Text fontSize="sm">{data.workflow?.name || 'None'}</Text>
              </Box>

              <Box>
                <HStack mb={1}>
                  <Icon as={InfoIcon} color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Broker
                  </Text>
                </HStack>
                <Text fontSize="sm">
                  {data.user
                    ? `${data.user.first_name} ${data.user.last_name}`
                    : 'Unassigned'}
                </Text>
              </Box>
            </Stack>

            <Stack spacing={4}>
              <Box>
                <HStack mb={1}>
                  <Icon as={InfoIcon} color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Age
                  </Text>
                </HStack>
                <Text fontSize="sm">
                  {getDateAge(new Date(data.created_at))}
                </Text>
              </Box>

              <Box>
                <HStack mb={1}>
                  <Icon as={InfoIcon} color="teal.500" />
                  <Text fontWeight="medium" color={textColor}>
                    Type
                  </Text>
                </HStack>
                <Text fontSize="sm">{data.type_display}</Text>
              </Box>
            </Stack>
          </Grid>
        </Card>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
        <Card p={5} h="100%">
          <Heading size="md" mb={4} color={textColor}>
            Details
          </Heading>
          <Divider mb={4} borderColor={borderColor} />

          <Box
            p={4}
            borderRadius="md"
            bg={noteBgColor}
            whiteSpace="pre-wrap"
            overflow="auto"
            height="100%"
            maxH="500px"
          >
            {data.details || 'No additional details available for this lead.'}
          </Box>
        </Card>

        <Box>
          <Card p={5}>
            <Heading size="md" mb={4} color={textColor}>
              Notes
              {canEditData && (
                <Box ml={2} display="inline-block">
                  <LeadAddNote lead={data} onSuccess={onNoteUpdate} />
                </Box>
              )}
            </Heading>
            <Divider mb={4} borderColor={borderColor} />

            <Box
              p={4}
              borderRadius="md"
              bg={noteBgColor}
              whiteSpace="pre-wrap"
              overflow="auto"
            >
              {data.notes || 'No notes available for this lead.'}
            </Box>
          </Card>

          <Card p={5} mt="20px">
            <Heading size="md" mb={4} color={textColor}>
              Files
              <Flex ml={2} display="inline-flex" gap={2}>
                {canEditData && (
                  <LeadFileUpload lead={data} onSuccess={onFileUploadSuccess} />
                )}
                {canEditData && data.type_display === 'Auto' && (
                  <Button
                    size="sm"
                    colorScheme="teal"
                    variant="outline"
                    onClick={handleOpenDashModal}
                    leftIcon={<Icon as={AttachmentIcon} />}
                  >
                    Add DASH Report
                  </Button>
                )}
              </Flex>
            </Heading>
            <Divider mb={4} borderColor={borderColor} />

            {data.files && data.files.length > 0 ? (
              <Stack spacing={3} pr={2}>
                {data.files.map((file) => {
                  const isDashReport = isDashReportFile(file.name);

                  return (
                    <Box
                      key={`file-${file.id}`}
                      p={3}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={isDashReport ? 'teal.200' : 'gray.200'}
                      _hover={{ bg: isDashReport ? 'teal.50' : 'gray.50' }}
                      bg={isDashReport ? 'teal.25' : 'transparent'}
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center">
                          <Icon
                            as={AttachmentIcon}
                            mr={3}
                            color={isDashReport ? 'teal.500' : 'blue.500'}
                          />
                          <Box>
                            <Flex align="center">
                              <Text fontWeight="medium" mr={2}>
                                {file.name}
                              </Text>
                              {isDashReport && (
                                <Box
                                  bg="teal.100"
                                  color="teal.800"
                                  px={2}
                                  py={0.5}
                                  borderRadius="full"
                                  fontSize="xs"
                                  fontWeight="bold"
                                >
                                  DASH
                                </Box>
                              )}
                            </Flex>
                            <Text fontSize="xs" color="gray.500">
                              {isDashReport ? 'Generated' : 'Uploaded'}{' '}
                              {new Date(file.created_at).toLocaleDateString()}
                              {file.user &&
                                `, By ${file.user.first_name} ${file.user.last_name}`}
                            </Text>
                          </Box>
                        </Flex>
                        <Link
                          href={file.file}
                          isExternal
                          color={isDashReport ? 'teal.500' : 'blue.500'}
                          download={file.name}
                        >
                          <Flex align="center">
                            <Icon as={DownloadIcon} mr={1} />
                            <Text>Download</Text>
                          </Flex>
                        </Link>
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Box p={4} borderRadius="md" bg={noteBgColor}>
                No files have been added yet.
              </Box>
            )}
          </Card>
        </Box>
      </Grid>

      {data.policy && (
        <Card p={5}>
          <Flex align="center" justify="space-between">
            <Heading size="md" color={textColor}>
              Validated Policy
            </Heading>
            <Link
              color="teal.500"
              fontWeight="bold"
              onClick={() =>
                navigate(`/admin/policies/validations/${data.policy}`)
              }
            >
              View Policy Validations
            </Link>
          </Flex>
        </Card>
      )}

      {[
        LEAD_STATUS_APPLICATION_IN_PROGRESS,
        LEAD_STATUS_WON,
        LEAD_STATUS_LOST,
        LEAD_STATUS_ARCHIVED,
      ].includes(data.status) && (
        <LeadSubmissions lead={data} openModal={shouldOpenSubmission} />
      )}

      <LeadLog />

      {/* DASH Report Modal */}
      {data.type_display === 'Auto' && (
        <LeadDashReportModal
          isOpen={isDashModalOpen}
          onClose={handleCloseDashModal}
          leadId={data.id}
          onSuccess={handleDashReportSuccess}
        />
      )}
    </Flex>
  );
};

export default LeadOverviewView;
